import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as AvailableIcon,
  Error as OutOfStockIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  LocationOn as LocationIcon,
  Schedule as ExpiryIcon,
  QrCode as LotIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import Breadcrumbs from '../../components/Breadcrumbs';

// Mock inventory data with real-time tracking
const mockInventoryData = [
  {
    sku: 'LIP-001',
    product: 'Lipoderma™',
    category: 'Aesthetics',
    available: 25,
    allocated: 8,
    onHand: 33,
    reorderPoint: 15,
    maxStock: 100,
    unitCost: 300.00,
    averageCost: 295.50,
    lastReceived: '2024-02-20',
    location: 'A1-B3-C2',
    lots: [
      { number: 'LOT240225A', quantity: 20, expiry: '2025-02-25', received: '2024-02-20' },
      { number: 'LOT240218B', quantity: 13, expiry: '2025-02-18', received: '2024-02-15' }
    ],
    movements: [
      { date: '2024-02-25', type: 'outbound', quantity: 2, reference: 'ORD-2024-001', reason: 'Customer Order' },
      { date: '2024-02-24', type: 'outbound', quantity: 3, reference: 'ORD-2024-002', reason: 'Customer Order' },
      { date: '2024-02-20', type: 'inbound', quantity: 20, reference: 'PO-2024-015', reason: 'Purchase Order' }
    ],
    turnover: 12.5, // days
    trend: 'up'
  },
  {
    sku: 'EQC-001',
    product: 'equicent™ CTM',
    category: 'Equine',
    available: 40,
    allocated: 12,
    onHand: 52,
    reorderPoint: 20,
    maxStock: 150,
    unitCost: 200.00,
    averageCost: 198.75,
    lastReceived: '2024-02-22',
    location: 'A2-B1-C3',
    lots: [
      { number: 'LOT240224B', quantity: 30, expiry: '2025-02-24', received: '2024-02-22' },
      { number: 'LOT240220C', quantity: 22, expiry: '2025-02-20', received: '2024-02-18' }
    ],
    movements: [
      { date: '2024-02-25', type: 'outbound', quantity: 1, reference: 'ORD-2024-001', reason: 'Customer Order' },
      { date: '2024-02-23', type: 'outbound', quantity: 5, reference: 'ORD-2024-003', reason: 'Customer Order' },
      { date: '2024-02-22', type: 'inbound', quantity: 30, reference: 'PO-2024-018', reason: 'Purchase Order' }
    ],
    turnover: 8.2, // days
    trend: 'stable'
  }
];

const InventoryTracker = () => {
  const { org } = useOrg();
  const navigate = useNavigate();
  const [inventory, setInventory] = useState(mockInventoryData);

  const getStockStatus = (item) => {
    if (item.available === 0) return 'out_of_stock';
    if (item.available <= item.reorderPoint) return 'low_stock';
    if (item.available >= item.maxStock * 0.8) return 'high_stock';
    return 'normal';
  };

  const getStockStatusColor = (status) => {
    switch (status) {
      case 'out_of_stock': return '#ef4444';
      case 'low_stock': return '#f59e0b';
      case 'high_stock': return '#3b82f6';
      case 'normal': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStockStatusLabel = (status) => {
    switch (status) {
      case 'out_of_stock': return 'Out of Stock';
      case 'low_stock': return 'Low Stock';
      case 'high_stock': return 'High Stock';
      case 'normal': return 'Normal';
      default: return 'Unknown';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUpIcon sx={{ color: '#10b981' }} />;
      case 'down': return <TrendingDownIcon sx={{ color: '#ef4444' }} />;
      default: return <AnalyticsIcon sx={{ color: '#6b7280' }} />;
    }
  };

  const getStockPercentage = (available, max) => {
    return Math.round((available / max) * 100);
  };

  const formatCurrency = (value) => `$${value?.toFixed(2) || 0}`;
  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

  // Calculate KPIs
  const totalItems = inventory.reduce((sum, item) => sum + item.onHand, 0);
  const totalValue = inventory.reduce((sum, item) => sum + (item.onHand * item.unitCost), 0);
  const lowStockItems = inventory.filter(item => getStockStatus(item) === 'low_stock').length;
  const outOfStockItems = inventory.filter(item => getStockStatus(item) === 'out_of_stock').length;

  return (
    <Box>

      {/* Stock Alerts */}
      {(lowStockItems > 0 || outOfStockItems > 0) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600 }}>
            Inventory Alert: {outOfStockItems} item(s) out of stock, {lowStockItems} item(s) low stock
          </Typography>
        </Alert>
      )}

      {/* KPI Dashboard */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, border: '1px solid #e5e7eb' }}>
            <Avatar sx={{ bgcolor: '#10b981', mx: 'auto', mb: 1 }}>
              <InventoryIcon />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
              {totalItems}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Items on Hand
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, border: '1px solid #e5e7eb' }}>
            <Avatar sx={{ bgcolor: '#3b82f6', mx: 'auto', mb: 1 }}>
              <TrendingUpIcon />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
              {formatCurrency(totalValue)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Inventory Value
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, border: '1px solid #e5e7eb' }}>
            <Avatar sx={{ bgcolor: '#f59e0b', mx: 'auto', mb: 1 }}>
              <WarningIcon />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
              {lowStockItems}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Low Stock Items
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ textAlign: 'center', p: 2, border: '1px solid #e5e7eb' }}>
            <Avatar sx={{ bgcolor: '#ef4444', mx: 'auto', mb: 1 }}>
              <OutOfStockIcon />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 0.5 }}>
              {outOfStockItems}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Out of Stock
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Inventory Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Current Inventory Levels</Typography>
            <Button
              variant="outlined"
              startIcon={<AnalyticsIcon />}
              onClick={() => navigate(`/internal/${org}/analytics`)}
              size="small"
              sx={{
                borderColor: '#039582',
                color: '#039582',
                '&:hover': { 
                  borderColor: '#027A6B',
                  bgcolor: '#f0fdfa'
                }
              }}
            >
              Advanced Analytics
            </Button>
          </Box>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>SKU</strong></TableCell>
                  <TableCell><strong>Product</strong></TableCell>
                  <TableCell><strong>Available</strong></TableCell>
                  <TableCell><strong>Allocated</strong></TableCell>
                  <TableCell><strong>On Hand</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Location</strong></TableCell>
                  <TableCell><strong>Value</strong></TableCell>
                  <TableCell><strong>Turnover</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventory.map((item) => {
                  const status = getStockStatus(item);
                  const stockPercentage = getStockPercentage(item.available, item.maxStock);
                  
                  return (
                    <TableRow key={item.sku} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                          {item.sku}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.product}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.category}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.available}
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={stockPercentage} 
                            sx={{ 
                              mt: 0.5,
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: '#e5e7eb',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getStockStatusColor(status)
                              }
                            }}
                          />
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.allocated}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.onHand}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip 
                          label={getStockStatusLabel(status)}
                          size="small"
                          sx={{ 
                            bgcolor: getStockStatusColor(status),
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {item.location}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(item.onHand * item.unitCost)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getTrendIcon(item.trend)}
                          <Typography variant="body2">
                            {item.turnover} days
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Recent Movements */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Recent Inventory Movements</Typography>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventory.flatMap(item => 
                  item.movements.map(movement => ({
                    ...movement,
                    sku: item.sku,
                    product: item.product
                  }))
                ).sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10).map((movement, index) => (
                  <TableRow key={index}>
                    <TableCell>{formatDate(movement.date)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{movement.sku}</TableCell>
                    <TableCell>
                      <Chip 
                        label={movement.type}
                        size="small"
                        color={movement.type === 'inbound' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{movement.quantity}</TableCell>
                    <TableCell>{movement.reference}</TableCell>
                    <TableCell>{movement.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InventoryTracker;
